import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	TextField,
	InputAdornment,
	CircularProgress,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	IconButton,
	Pagination,
	FormControl,
	Select,
	MenuItem,
	SelectChangeEvent,
	Alert,
	Tooltip,
} from "@mui/material";
import {
	Add as AddIcon,
	Search as SearchIcon,
	Edit as EditIcon,
	Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEnvironmentNavigation } from "../../hooks/useEnvironmentNavigation";
import {
	useCompanyGroups,
	useSearchCompanyGroups,
} from "../../hooks/useCompanyGroup";
import { zoullooColors } from "../../theme";

const CompanyGroupList: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { navigateByEnvironment } = useEnvironmentNavigation();

	const [searchTerm, setSearchTerm] = useState("");
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchError, setSearchError] = useState("");

	const {
		data: initialData,
		isLoading: isLoadingInitial,
		error: initialError,
	} = useCompanyGroups(page, pageSize);

	const {
		data: searchData,
		isLoading: isSearching,
		error: searchApiError,
		isSearched,
		searchCompanyGroups,
		resetSearch,
	} = useSearchCompanyGroups();

	const data = isSearched ? searchData : initialData;
	const isLoading = isLoadingInitial || isSearching;
	const error = isSearched ? searchApiError : initialError;

	const canSearch = useMemo(() => {
		return searchTerm.trim().length >= 2;
	}, [searchTerm]);

	useEffect(() => {
		if (isSearched && searchTerm.trim() === "") {
			resetSearch();
		}
	}, [searchTerm, isSearched, resetSearch]);

	const companyGroups = data?.items || [];
	const totalCount = data?.totalCount || 0;
	const totalPages = data?.totalPages || 0;

	const handleCreate = () => {
		navigate("/company-groups/create");
	};

	const handleView = (id: number) => {
		navigateByEnvironment(`/company-groups/${id}`);
	};

	const handleEdit = (id: number) => {
		navigateByEnvironment(`/company-groups/${id}/edit`);
	};

	const handleSearch = () => {
		setSearchError("");

		if (!canSearch) {
			setSearchError(t("companyGroup.searchMinChars"));
			return;
		}

		setPage(1);
		searchCompanyGroups(searchTerm, 1, pageSize);
	};

	const handleClearSearch = () => {
		setSearchTerm("");
		setSearchError("");
		setPage(1);
		resetSearch();
	};

	const handlePageChange = (
		_event: React.ChangeEvent<unknown>,
		value: number
	) => {
		setPage(value);
		if (isSearched) {
			searchCompanyGroups(searchTerm, value, pageSize);
		}
	};

	const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
		const newSize = Number(event.target.value);
		setPageSize(newSize);
		setPage(1);

		if (isSearched) {
			searchCompanyGroups(searchTerm, 1, newSize);
		}
	};

	const handleKeyPress = (event: React.KeyboardEvent) => {
		if (event.key === "Enter" && canSearch) {
			handleSearch();
		}
	};

	if (isLoading && !isSearched) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error && !isSearching) {
		return (
			<Box p={3}>
				<Typography color="error">
					{t("common.errorLoading")}: {error.message}
				</Typography>
			</Box>
		);
	}

	return (
		<Box px={6} py={4}>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
				<Box>
					<Typography variant="h2">{t("companyGroup.list")}</Typography>
					<Typography variant="body1" color="text.secondary" mt={1}>
						{t("client.showing")} {totalCount}{" "}
						{t("companyGroup.groups").toLowerCase()}
					</Typography>
				</Box>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={handleCreate}
					sx={{
						backgroundColor: zoullooColors.secondary.main,
						color: zoullooColors.secondary.contrastText,
						"&:hover": {
							backgroundColor: zoullooColors.secondary.dark,
						},
					}}
				>
					{t("companyGroup.createNew")}
				</Button>
			</Box>

			<Paper elevation={3} sx={{ p: 3, mb: 3 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<Box sx={{ flex: "1 1 auto" }}>
						<TextField
							fullWidth
							variant="outlined"
							placeholder={t("companyGroup.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => {
								setSearchTerm(e.target.value);
								setSearchError("");
							}}
							onKeyPress={handleKeyPress}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon />
									</InputAdornment>
								),
								sx: { height: "40px" },
							}}
							error={!!searchError}
							helperText={searchError}
							size="small"
						/>
					</Box>

					<Box sx={{ display: "flex", gap: 1, alignItems: "center", flexShrink: 0 }}>
						<Tooltip title={!canSearch ? t("companyGroup.searchMinChars") : ""} arrow>
							<span>
								<Button
									variant="outlined"
									onClick={handleSearch}
									disabled={isSearching || !canSearch}
									sx={{
										height: "40px",
										borderColor: zoullooColors.secondary.main,
										color: zoullooColors.secondary.main,
										backgroundColor: zoullooColors.background.default,
										"&:hover": {
											borderColor: zoullooColors.secondary.dark,
											backgroundColor: zoullooColors.secondary.alpha,
										},
										opacity: !canSearch && !isSearching ? 0.7 : 1,
									}}
								>
									{isSearching ? (
										<CircularProgress size={20} color="inherit" />
									) : (
										t("common.search")
									)}
								</Button>
							</span>
						</Tooltip>

						{isSearched && (
							<Button
								variant="outlined"
								onClick={handleClearSearch}
								sx={{
									height: "40px",
									borderColor: zoullooColors.secondary.main,
									color: zoullooColors.secondary.main,
									"&:hover": {
										borderColor: zoullooColors.secondary.dark,
										backgroundColor: zoullooColors.secondary.alpha,
									},
								}}
							>
								{t("common.clear")}
							</Button>
						)}
					</Box>
				</Box>
			</Paper>

			<Box display="flex" justifyContent="flex-end" alignItems="center" mb={3}>
				<FormControl size="small">
					<Select value={pageSize} onChange={handlePageSizeChange} displayEmpty>
						<MenuItem value={10}>10 {t("common.perPage")}</MenuItem>
						<MenuItem value={20}>20 {t("common.perPage")}</MenuItem>
						<MenuItem value={50}>50 {t("common.perPage")}</MenuItem>
					</Select>
				</FormControl>
			</Box>

			{isSearching && (
				<Box display="flex" justifyContent="center" my={4}>
					<CircularProgress />
				</Box>
			)}

			{isSearched && !isSearching && companyGroups.length === 0 && (
				<Alert severity="info" sx={{ mb: 3 }}>
					{t("companyGroup.noSearchResults", { searchTerm })}
				</Alert>
			)}

			{!isSearching && (
				<TableContainer component={Paper} elevation={3}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>{t("companyGroup.name")}</TableCell>
								<TableCell>{t("companyGroup.website")}</TableCell>
								<TableCell>{t("companyGroup.companiesCount")}</TableCell>
								<TableCell>{t("common.actions")}</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{companyGroups.length > 0 ? (
								companyGroups.map((group) => (
									<TableRow key={group.id} hover>
										<TableCell>{group.name}</TableCell>
										<TableCell>
											{group.website ? (
												<a
													href={group.website}
													target="_blank"
													rel="noopener noreferrer"
												>
													{group.website}
												</a>
											) : (
												"-"
											)}
										</TableCell>
										<TableCell>{group.companies?.length || 0}</TableCell>
										<TableCell>
											<IconButton
												size="small"
												onClick={() => handleView(group.id)}
												color="secondary"
											>
												<VisibilityIcon />
											</IconButton>
											<IconButton
												size="small"
												onClick={() => handleEdit(group.id)}
												color="primary"
											>
												<EditIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										{isSearched
											? t("companyGroup.noSearchResults", { searchTerm })
											: t("companyGroup.noGroupsFound")}
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			)}

			{totalPages > 0 && !isSearching && (
				<Box display="flex" justifyContent="center" alignItems="center" mt={4} gap={2}>
					<Pagination
						count={totalPages}
						page={page}
						onChange={handlePageChange}
						showFirstButton
						showLastButton
						sx={{
							"& .MuiPaginationItem-root.Mui-selected": {
								backgroundColor: zoullooColors.secondary.main,
								color: zoullooColors.text.white,
								"&:hover": {
									backgroundColor: zoullooColors.secondary.dark,
								},
							},
						}}
					/>
				</Box>
			)}
		</Box>
	);
};

export default CompanyGroupList;
